import React, { useContext, useEffect, useRef, useState } from 'react';
import { Table, Button, Space, message, Input, Tag } from 'antd';
import { fetchAllSubmissions } from '../../api';
import { AuthContext } from '../../AuthContext';
import { EditOutlined, SearchOutlined, UserOutlined, DownloadOutlined } from '@ant-design/icons';
import { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import type { ColumnsType } from 'antd/es/table';
import type { InputRef } from 'antd';
import * as XLSX from 'xlsx';

interface DataType {
    key: string;
    username: string;
    referenceNumber: string;
    travelAgentName: string;
    travelAgentLicense: string;
    registrationNumber: string;
    travelAgentEmail: string;
    submitDate: string;
    arrivalDate?: string;
}

type DataIndex = keyof DataType;

const AllSubmissionsPage = () => {
    const authContext = useContext(AuthContext);
    const { setLoggedIn } = authContext || {};
    const [submissions, setSubmissions] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const [globalSearchText, setGlobalSearchText] = useState('');
    const [usernameFilter, setUsernameFilter] = useState('');
    const [sortField, setSortField] = useState<string | undefined>(undefined);
    const [sortOrder, setSortOrder] = useState<'ascend' | 'descend' | undefined>(undefined);
    const searchInput = useRef<InputRef>(null);

    async function fetchData(page: number = currentPage, limit: number = pageSize, search?: string, username?: string, sortFieldParam?: string, sortOrderParam?: string) {
        setLoading(true);
        try {
            const searchParam = search !== undefined ? search : globalSearchText;
            const usernameParam = username !== undefined ? username : usernameFilter;
            const sortFieldToUse = sortFieldParam !== undefined ? sortFieldParam : sortField;
            const sortOrderToUse = sortOrderParam !== undefined ? sortOrderParam : sortOrder;
            const response = await fetchAllSubmissions(page, limit, searchParam || undefined, usernameParam || undefined, sortFieldToUse, sortOrderToUse);
            if (response.status === 401) {
                if (setLoggedIn) {
                    setLoggedIn(false);
                }
            }
            else if (response.status === 403) {
                message.error('您沒有權限訪問此頁面');
                return;
            }
            else if (response.status !== 200) {
                console.log('Failed to fetch submissions');
                return;
            }
            const result = await response.json();
            if (!result.success) {
                console.log('Failed to fetch submissions');
                return;
            }
            setSubmissions(result.data);
            setTotal(result.total || 0);
            setCurrentPage(result.page || page);
            setPageSize(result.limit || limit);
        } catch (err) {
            console.log('Failed to fetch submissions');
            message.error('無法取得提交紀錄');
            setSubmissions([]);
        } finally {
            setLoading(false);
        }
    }

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
        setGlobalSearchText(selectedKeys[0]);
        fetchData(1, pageSize, selectedKeys[0], usernameFilter);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
        setGlobalSearchText('');
        fetchData(1, pageSize, '', usernameFilter);
    };

    const handleUsernameFilter = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
    ) => {
        confirm();
        const username = selectedKeys[0];
        setUsernameFilter(username);
        fetchData(1, pageSize, globalSearchText, username);
    };

    const handleUsernameReset = (clearFilters: () => void) => {
        clearFilters();
        setUsernameFilter('');
        fetchData(1, pageSize, globalSearchText, '');
    };

    const getColumnTitle = (dataIndex: DataIndex): string => {
        const titles: Record<DataIndex, string> = {
            key: '鍵',
            username: '用戶名稱',
            referenceNumber: '參考編號',
            travelAgentName: '持牌旅行代理商名稱',
            travelAgentLicense: '旅行代理商牌照號碼',
            registrationNumber: '登記貼紙編號',
            travelAgentEmail: '電郵地址',
            submitDate: '提交時間',
            arrivalDate: '抵港日期',
        };
        return titles[dataIndex] || dataIndex;
    };

    const getColumnSearchProps = (dataIndex: DataIndex) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }: FilterDropdownProps) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`搜尋 ${getColumnTitle(dataIndex)}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        搜尋
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        重置
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        關閉
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilterDropdownOpenChange: (visible: boolean) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text: string) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const getUsernameFilterProps = () => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }: FilterDropdownProps) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    placeholder="搜尋用戶名稱"
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleUsernameFilter(selectedKeys as string[], confirm)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleUsernameFilter(selectedKeys as string[], confirm)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        搜尋
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleUsernameReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        重置
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        關閉
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <UserOutlined style={{ color: usernameFilter ? '#1677ff' : undefined }} />
        ),
    });

    const columns: ColumnsType<DataType> = [
        {
            title: '用戶名稱',
            dataIndex: 'username',
            key: 'username',
            width: 150,
            fixed: 'left',
            sorter: true,
            showSorterTooltip: false,
            ...getUsernameFilterProps(),
            render: (text: string) => (
                <Tag color="blue" icon={<UserOutlined />} style={{ fontWeight: '500' }}>
                    {text}
                </Tag>
            ),
        },
        {
            title: '參考編號',
            dataIndex: 'referenceNumber',
            key: 'referenceNumber',
            width: 180,
            sorter: true,
            showSorterTooltip: false,
            ...getColumnSearchProps('referenceNumber'),
            render: (text: string) => (
                <span style={{ fontWeight: '500', color: '#2c3e50' }}>{text}</span>
            ),
        },
        {
            title: '持牌旅行代理商名稱',
            dataIndex: 'travelAgentName',
            key: 'travelAgentName',
            width: 220,
            sorter: true,
            showSorterTooltip: false,
            ...getColumnSearchProps('travelAgentName'),
            render: (text: string) => (
                <span style={{ color: '#2c3e50' }}>{text}</span>
            ),
        },
        {
            title: '旅行代理商牌照號碼',
            dataIndex: 'travelAgentLicense',
            key: 'travelAgentLicense',
            width: 200,
            sorter: true,
            showSorterTooltip: false,
            ...getColumnSearchProps('travelAgentLicense'),
            render: (text: string) => (
                <span style={{ color: '#5a6c7d' }}>{text}</span>
            ),
        },
        {
            title: '登記貼紙編號',
            dataIndex: 'registrationNumber',
            key: 'registrationNumber',
            width: 180,
            sorter: true,
            showSorterTooltip: false,
            ...getColumnSearchProps('registrationNumber'),
            render: (text: string) => (
                <span style={{ color: '#5a6c7d' }}>{text}</span>
            ),
        },
        {
            title: '電郵地址',
            dataIndex: 'travelAgentEmail',
            key: 'travelAgentEmail',
            width: 220,
            sorter: true,
            showSorterTooltip: false,
            ...getColumnSearchProps('travelAgentEmail'),
            render: (text: string) => (
                <span style={{ color: '#5a6c7d' }}>{text}</span>
            ),
        },
        {
            title: '抵港日期',
            dataIndex: 'arrivalDate',
            key: 'arrivalDate',
            width: 150,
            sorter: true,
            showSorterTooltip: false,
            ...getColumnSearchProps('arrivalDate'),
            render: (text: string) => (
                <span style={{ color: '#5a6c7d' }}>{text}</span>
            ),
        },
        {
            title: '提交時間',
            dataIndex: 'submitDate',
            key: 'submitDate',
            width: 180,
            sorter: true,
            showSorterTooltip: false,
            ...getColumnSearchProps('submitDate'),
            render: (text: string) => (
                <span style={{ color: '#5a6c7d' }}>{text}</span>
            ),
        },
        {
            title: '動作',
            key: 'action',
            width: 100,
            fixed: 'right',
            render: (text: string, record: any) => (
                <Space size="middle">
                    <Button icon={<EditOutlined />} onClick={() => handleEdit(record.id)}>查看</Button>
                </Space>
            ),
        }
    ];

    const handleEdit = (key: string) => {
        const item = submissions.find((item) => item.id === key);
        const encoded = btoa(JSON.stringify({
            referenceNumber: item.referenceNumber,
            registrationNumber: item.registrationNumber,
            travelAgentEmail: item.travelAgentEmail,
        }));
        window.open(`/form/${encoded}`, '_blank');
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleTableChange = (pagination: any, filters: any, sorter: any) => {
        // Get pagination values
        const newPage = pagination.current || currentPage;
        const newPageSize = pagination.pageSize || pageSize;
        
        // Handle sorting
        let newSortField = sortField;
        let newSortOrder = sortOrder;
        
        if (sorter && sorter.field) {
            newSortField = sorter.field as string;
            newSortOrder = sorter.order as 'ascend' | 'descend' | undefined;
            setSortField(newSortField);
            setSortOrder(newSortOrder);
        } else if (sorter && !sorter.order) {
            // Clear sorting
            newSortField = undefined;
            newSortOrder = undefined;
            setSortField(undefined);
            setSortOrder(undefined);
        }
        
        // Update state and fetch data once
        setCurrentPage(newPage);
        setPageSize(newPageSize);
        fetchData(newPage, newPageSize, globalSearchText, usernameFilter, newSortField, newSortOrder);
    };

    const handleExport = async () => {
        try {
            setLoading(true);
            // Fetch all records without pagination
            const response = await fetchAllSubmissions(1, 999999, globalSearchText || undefined, usernameFilter || undefined);
            if (response.status !== 200) {
                message.error('無法取得數據');
                return;
            }
            const result = await response.json();
            if (!result.success) {
                message.error('無法取得數據');
                return;
            }

            // Prepare data for export
            const exportData = result.data.map((record: any) => ({
                '用戶名稱': record.username,
                '參考編號': record.referenceNumber,
                '持牌旅行代理商名稱': record.travelAgentName,
                '旅行代理商牌照號碼': record.travelAgentLicense,
                '登記貼紙編號': record.registrationNumber,
                '電郵地址': record.travelAgentEmail,
                '抵港日期': record.arrivalDate || '',
                '提交時間': record.submitDate,
            }));

            // Create workbook and worksheet
            const ws = XLSX.utils.json_to_sheet(exportData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, '提交紀錄');

            // Generate filename with current date
            const date = new Date();
            const filename = `所有提交紀錄_${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}.xlsx`;

            // Save file
            XLSX.writeFile(wb, filename);
            message.success('導出成功');
        } catch (err) {
            console.error('Export failed:', err);
            message.error('導出失敗');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ padding: '32px', maxWidth: '1800px', margin: '0 auto' }}>
            <div style={{
                marginBottom: '24px',
                padding: '20px 24px',
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
            }}>
                <div>
                    <h2 style={{
                        margin: 0,
                        fontSize: '26px',
                        fontWeight: '700',
                        color: '#1a1a1a',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                    }}>所有提交紀錄</h2>
                    <p style={{ margin: '8px 0 0 0', color: '#5a6c7d' }}>
                        管理和查看所有用戶的提交記錄
                    </p>
                </div>
                <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={handleExport}
                    loading={loading}
                    style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        border: 'none',
                        height: '40px',
                        fontSize: '16px',
                    }}
                >
                    導出記錄
                </Button>
            </div>
            <div style={{
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
            }}>
                <Table
                    rowKey="id"
                    columns={columns}
                    dataSource={submissions}
                    size="small"
                    loading={loading}
                    scroll={{ x: 1600 }}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: total,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        pageSizeOptions: ['10', '20', '30', '50', '100'],
                        showTotal: (total) => `總共 ${total} 條記錄`,
                    }}
                    onChange={handleTableChange}
                />
            </div>
        </div>
    );
};

export default AllSubmissionsPage;
